'use client';

/**
 * Notification Provider Component
 * 
 * Initializes notification services and provides toast notifications.
 * Should be placed in the root layout to enable notifications app-wide.
 */

import React, { useEffect, useMemo } from 'react';
import { Toaster } from 'sonner';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import { notificationService } from '../services/notificationService';
import { useSignalR } from '../hooks/useSignalR';
import { API_BASE_URL } from '@/app/constants/apiConstants';
import { detectSystemTheme } from '@/app/features/theme/utils/simpleThemeUtils';
import { tokenManager } from '@/app/services/auth/tokenManager';

/**
 * Notification Provider Props
 */
interface NotificationProviderProps {
  children?: React.ReactNode;
}

/**
 * Notification Provider Component
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const themeMode = useSelector((state: RootState) => state.theme.mode);
  // Read preferences from userPreferences slice, not auth slice
  const preferences = useSelector((state: RootState) => state.userPreferences.preferences);
  
  const [systemTheme, setSystemTheme] = React.useState<'light' | 'dark'>(() => 
    typeof window !== 'undefined' ? detectSystemTheme() : 'light'
  );

  /**
   * Listen for system theme changes when in system mode
   */
  useEffect(() => {
    if (typeof window === 'undefined' || themeMode !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeMode]);

  /**
   * Determine the effective theme for Sonner
   * Resolves 'system' to actual 'light' or 'dark'
   */
  const effectiveTheme = useMemo(() => {
    if (themeMode === 'system') {
      return systemTheme;
    }
    return themeMode;
  }, [themeMode, systemTheme]);

  /**
   * Get toast position from user preferences
   * Default to 'top-right' if not set
   */
  const toastPosition = useMemo(() => {
    return preferences?.notificationToastPosition || 'bottom-right';
  }, [preferences?.notificationToastPosition]);

  /**
   * Get toast duration from user preferences
   * Convert from seconds to milliseconds
   * 0 = never auto-dismiss (Infinity)
   * Default to 5 seconds if not set
   */
  const toastDuration = useMemo(() => {
    const durationInSeconds = preferences?.notificationToastDuration ?? 5;
    // If 0, return Infinity (never auto-dismiss)
    if (durationInSeconds === 0) {
      return Infinity;
    }
    // Convert seconds to milliseconds
    return durationInSeconds * 1000;
  }, [preferences?.notificationToastDuration]);

  /**
   * Check if toast notifications are enabled
   * Default to true if not set
   */
  const toastEnabled = useMemo(() => {
    return preferences?.notificationToastEnabled !== false;
  }, [preferences?.notificationToastEnabled]);

  /**
   * Debug logging for preferences
   */
  useEffect(() => {
    console.log('🔔 [NotificationProvider] Preferences updated:', {
      enabled: toastEnabled,
      position: toastPosition,
      durationMs: toastDuration,
      durationSeconds: preferences?.notificationToastDuration,
      soundEnabled: preferences?.notificationSoundEnabled,
      hasPreferences: !!preferences,
      isAuthenticated
    });
  }, [toastEnabled, toastPosition, toastDuration, isAuthenticated]);

  /**
   * Get access token using tokenManager
   * (Centralized token management for consistency)
   */
  const getAccessToken = React.useCallback(() => {
    if (typeof window === 'undefined') return null;
    const tokens = tokenManager.getTokens();
    return tokens?.accessToken || null;
  }, []);

  // Initialize SignalR connection (centralized control)
  // This hook manages the connection lifecycle independently of re-renders
  useSignalR({
    autoConnect: true,
    showToast: toastEnabled,
    playSound: preferences?.notificationSoundEnabled !== false,
  });

  /**
   * Initialize notification service
   */
  useEffect(() => {
    notificationService.initialize({
      baseURL: API_BASE_URL,
      getAccessToken,
    });

    console.log('[NotificationProvider] Notification service initialized with API URL:', API_BASE_URL);
  }, [getAccessToken]);

  return (
    <>
      {children}
      {toastEnabled && (
        <Toaster
          position={toastPosition as any}
          expand={true}
          richColors
          closeButton
          duration={toastDuration}
          theme={effectiveTheme}
          offset="64px"
          // Sonner's default z-index (~100) renders BEHIND the app's Radix
          // Dialog overlay (zIndex 9999999998) + content (9999999999),
          // making error toasts invisible whenever a modal is open. Pin the
          // toaster viewport one above the dialog content so toast feedback
          // is always readable, regardless of which modal is open.
          // Per `feedback_dialog_ten_nines_zindex_trap.md` — the 10-nines
          // pattern is the project-wide convention for Radix overlays that
          // must beat shadcn defaults; the toast must beat that.
          style={{ zIndex: 10_000_000_000 } as React.CSSProperties}
        />
      )}
    </>
  );
}

